import { Flex, FormItemsGroup, Input, Space, Text } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { DashboardNamePhoneStep } from '../../../../../../assets/images'
import { useDebouncedCallback } from '../../../../../../hooks/useDebouncedCallback'
import { usePhoneDialCodePlaceholder } from '../../../../../../hooks/usePhoneDialCodePlaceholder'
import { useOnboardingFormContext } from '../../../../contexts/OnboardingFormContext'
import { useRegisterOnboardingStep } from '../../../../hooks/useRegisterOnboardingStep'
import { useTrackOnboarding } from '../../../../hooks/useTrackOnboarding'
import { OnboardingForm } from '../../../../types/onboardingForm'
import { StepContainer } from '../StepContainer'
import { usePredefinedNamePhoneStepValues } from './hooks/usePredefinedNamePhoneStepValues'
import { useSubmitNamePhoneStepForm } from './hooks/useSubmitNamePhoneStepForm'

export const NamePhoneStep = (): ReactElement => {
  const { t } = useTranslation()

  const { isLoading, onboardingForm } = useOnboardingFormContext()
  const { FormItem } = onboardingForm

  const { isLoading: isLoadingPredefinedValues, predefinedValues } = usePredefinedNamePhoneStepValues()
  const { isSubmitting, submitForm } = useSubmitNamePhoneStepForm()
  const { trackOnboarding } = useTrackOnboarding()
  const phoneDialCodePlaceholder = usePhoneDialCodePlaceholder()

  const handleDebouncedNameTyped = useDebouncedCallback(() => trackOnboarding('Full Name typed'))
  const handleDebouncedPhoneTyped = useDebouncedCallback(() => trackOnboarding('Phone number typed'))

  const submitHandler = useCallback(
    async (values: OnboardingForm) => {
      await submitForm(values)
    },
    [submitForm],
  )

  useRegisterOnboardingStep({
    isLoading: isLoadingPredefinedValues,
    isSubmitting,
    predefinedValues,
    submitHandler,
  })

  return (
    <StepContainer image={<DashboardNamePhoneStep />} title={t('onboarding.steps.namePhone.title')}>
      <Flex flexDirection="column">
        <FormItemsGroup>
          <FormItem
            name="name"
            label={t('onboarding.steps.namePhone.name.label')}
            render={({ field: { onChange, ...rest } }) => (
              <Input
                {...rest}
                disabled={isLoading}
                onChange={(event) => {
                  onChange(event)
                  handleDebouncedNameTyped()
                }}
              />
            )}
          />
          <Space size="s8" />
          <FormItem
            name="phone"
            label={t('onboarding.steps.namePhone.phone.label')}
            render={({ field: { onChange, ...rest } }) => (
              <Input
                {...rest}
                disabled={isLoading}
                onChange={(event) => {
                  onChange(event)
                  handleDebouncedPhoneTyped()
                }}
                placeholder={phoneDialCodePlaceholder}
              />
            )}
          />
          <Text variant="bodyMD">{t('onboarding.steps.namePhone.phone.message')}</Text>
        </FormItemsGroup>
      </Flex>
    </StepContainer>
  )
}
